import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { Send, Clock, Check } from "lucide-react";
import OverwriteTestTimeService, {
  IdName,
  TestDataBean,
  ActivationListResponse,
} from "../../_services/OverwriteTestTimeService";
import "./overwrite-test-time.css";

const STATUS_COMPLETED = 3;
const STATUS_IN_PROGRESS = 1;

/** Backend "dd-MM-yyyy HH:mm:ss" → datetime-local input value "yyyy-MM-ddTHH:mm:ss". */
function toLocalInput(value: string): string {
  const m = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec((value || "").trim());
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}:${m[6] || "00"}`;
}

/** datetime-local input value "yyyy-MM-ddTHH:mm[:ss]" → backend "dd-MM-yyyy HH:mm:ss". */
function fromLocalInput(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec((value || "").trim());
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}:${m[6] || "00"}`;
}

interface ConfirmState {
  show: boolean;
  message: string;
  onConfirm: (() => void) | null;
}

interface AlertState {
  show: boolean;
  title: string;
  message: string;
  success: boolean;
  details?: { label: string; value: string }[];
}

const OverwriteTestTimePage: React.FC = () => {
  // Form dropdowns
  const [years, setYears] = useState<IdName[]>([]);
  const [categories, setCategories] = useState<IdName[]>([]);
  const [tests, setTests] = useState<IdName[]>([]);

  // Form fields
  const [year, setYear] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [test, setTest] = useState<string>("");
  const [enquiryId, setEnquiryId] = useState<string>("");
  const [studentCode, setStudentCode] = useState<string>("");
  const [hallTicket, setHallTicket] = useState<string>("");
  const [submitStatus, setSubmitStatus] = useState<number>(STATUS_COMPLETED);

  const [yearError, setYearError] = useState<string>("");
  const [testError, setTestError] = useState<string>("");

  // Results
  const [result, setResult] = useState<ActivationListResponse | null>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [rowInputs, setRowInputs] = useState<Record<number, { elapsedTime: string; cutoff: string }>>({});
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});

  // Add Extra Time modal (shown when 0 records)
  const [extraModal, setExtraModal] = useState<boolean>(false);
  const [extraStudentCode, setExtraStudentCode] = useState<string>("");
  const [extraCutoff, setExtraCutoff] = useState<string>("");

  // Confirm + alert modals (replace bootbox/window.confirm)
  const [confirm, setConfirm] = useState<ConfirmState>({ show: false, message: "", onConfirm: null });
  const [alertModal, setAlertModal] = useState<AlertState>({ show: false, title: "", message: "", success: true });

  useEffect(() => {
    OverwriteTestTimeService.getYearsAndCategories()
      .then(({ years, testCategories }) => {
        setYears(years);
        setCategories(testCategories);
      })
      .catch(() => {
        setAlertModal({ show: true, title: "Error", message: "Failed to load years and categories.", success: false });
      });
  }, []);

  const loadTests = (yearId: string, categoryId: string) => {
    if (!yearId || Number(yearId) < 1) {
      setTests([]);
      return;
    }
    OverwriteTestTimeService.getTests(Number(yearId), Number(categoryId) || 0)
      .then(setTests)
      .catch(() => setTests([]));
  };

  const handleYearChange = (value: string) => {
    setYear(value);
    loadTests(value, category);
  };

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    loadTests(year, value);
  };

  const handleSearch = async () => {
    let invalid = false;
    if (!year || Number(year) < 1) { setYearError("Required"); invalid = true; } else setYearError("");
    if (!test || Number(test) < 1) { setTestError("Required"); invalid = true; } else setTestError("");
    if (invalid) return;

    setSearching(true);
    setResult(null);
    try {
      const data = await OverwriteTestTimeService.fetchActivationList({
        year: Number(year),
        testCategory: Number(category) || 0,
        test: Number(test),
        enquiryId: enquiryId.trim() || undefined,
        studentCode: studentCode.trim() || undefined,
        hallTicket: hallTicket.trim() || undefined,
        testSubmitStatus: submitStatus,
      });
      setResult(data);
      // Seed each row's cut-off with the test end date (Java default = bean.endDate).
      const seeded: Record<number, { elapsedTime: string; cutoff: string }> = {};
      (data.testDataBeans || []).forEach((b, idx) => {
        seeded[idx] = { elapsedTime: "", cutoff: b.endDate || "" };
      });
      setRowInputs(seeded);
      setRowErrors({});
    } catch {
      setAlertModal({ show: true, title: "Error", message: "Failed to fetch the student list.", success: false });
    } finally {
      setSearching(false);
    }
  };

  const setRowInput = (idx: number, patch: Partial<{ elapsedTime: string; cutoff: string }>) => {
    setRowInputs((prev) => ({ ...prev, [idx]: { ...prev[idx], ...patch } }));
  };

  const doGrace = async (
    params: { testId: number; enquiryId?: number; studentCode?: string; elapsedTime?: number; cutoffDateTime: string },
    onSuccess: () => void,
    successDetails?: { label: string; value: string }[],
  ) => {
    try {
      const resp = await OverwriteTestTimeService.provideGraceTime(params);
      setAlertModal({
        show: true,
        title: "Confirmation",
        message: resp.message,
        success: resp.type === 1,
        details: resp.type === 1 ? successDetails : undefined,
      });
      if (resp.type === 1) onSuccess();
    } catch {
      setAlertModal({ show: true, title: "Error", message: "Failed to provide extra time.", success: false });
    }
  };

  const handleActivate = (bean: TestDataBean, idx: number) => {
    const testTime = Number(result?.testTime ?? bean.testTime) || 0;
    const inputs = rowInputs[idx] || { elapsedTime: "", cutoff: "" };
    const elapsed = Number(inputs.elapsedTime);

    let valid = testTime === 0 ? true : elapsed > 0 && elapsed <= testTime;
    if (!valid) {
      setRowErrors((p) => ({ ...p, [idx]: `Should be > 0 and <= ${testTime} mins` }));
      return;
    }
    if (!inputs.cutoff || !inputs.cutoff.trim()) {
      setRowErrors((p) => ({ ...p, [idx]: "Cutoff time should be entered" }));
      return;
    }
    setRowErrors((p) => ({ ...p, [idx]: "" }));

    const who = bean.keyword && bean.keyword !== "--"
      ? `${bean.name} (${bean.keyword})`
      : bean.name;
    setConfirm({
      show: true,
      message: `Are you sure you want to Activate the Test for ${who}?`,
      onConfirm: () => {
        setConfirm({ show: false, message: "", onConfirm: null });
        doGrace(
          {
            testId: bean.id,
            enquiryId: bean.enquiryId,
            elapsedTime: testTime === 0 ? 0 : elapsed,
            cutoffDateTime: inputs.cutoff.trim(),
          },
          () => handleSearch(),
          [
            { label: "Student Name", value: bean.name },
            { label: "Student Code", value: bean.keyword && bean.keyword !== "--" ? bean.keyword : "--" },
            { label: "Extra Time", value: testTime === 0 ? "—" : `${elapsed} min` },
            { label: "Cut Off", value: inputs.cutoff.trim() },
          ],
        );
      },
    });
  };

  const handleAddExtraTime = () => {
    if (!extraStudentCode.trim() || !extraCutoff.trim()) {
      setAlertModal({ show: true, title: "Validation", message: "Please enter proper details.", success: false });
      return;
    }
    setConfirm({
      show: true,
      message: `Are you sure you want to Activate the Test for Student Code ${extraStudentCode.trim()}?`,
      onConfirm: () => {
        setConfirm({ show: false, message: "", onConfirm: null });
        doGrace(
          {
            testId: result?.testId || Number(test),
            studentCode: extraStudentCode.trim(),
            cutoffDateTime: extraCutoff.trim(),
          },
          () => {
            setExtraModal(false);
            setExtraStudentCode("");
            setExtraCutoff("");
            handleSearch();
          },
          [
            { label: "Student Code", value: extraStudentCode.trim() },
            { label: "Cut Off", value: extraCutoff.trim() },
          ],
        );
      },
    });
  };

  const beans = result?.testDataBeans || [];
  const showTime = !!result?.showTime;
  const sc = result?.searchCriteria;

  return (
    <div className="overwrite-test-time">
      <div className="ott-card">
        <div className="ott-card-header">Overwrite Test Time</div>
        <div className="ott-card-body">
          {/* Search form */}
          <div className="ott-form-grid">
            <div className="ott-field">
              <label>Year</label>
              <select className="ott-input" value={year} onChange={(e) => handleYearChange(e.target.value)}>
                <option value="">--Select--</option>
                {years.map((y) => (
                  <option key={y.id} value={y.id}>{y.name}</option>
                ))}
              </select>
              {yearError && <span className="ott-error">{yearError}</span>}
            </div>

            <div className="ott-field">
              <label>Category</label>
              <select className="ott-input" value={category} onChange={(e) => handleCategoryChange(e.target.value)}>
                <option value="">--Select--</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="ott-field">
              <label>Test</label>
              <select className="ott-input" value={test} onChange={(e) => setTest(e.target.value)}>
                <option value="">--Select--</option>
                {tests.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} [{t.id}]</option>
                ))}
              </select>
              {testError && <span className="ott-error">{testError}</span>}
            </div>

            <div className="ott-field">
              <label>Enquiry Id</label>
              <input className="ott-input" placeholder="Enquiry Id" value={enquiryId} onChange={(e) => setEnquiryId(e.target.value)} />
            </div>

            <div className="ott-field">
              <label>Student Code</label>
              <input className="ott-input" placeholder="Student Code" value={studentCode} onChange={(e) => setStudentCode(e.target.value)} />
            </div>

            <div className="ott-field">
              <label>HallTicket</label>
              <input className="ott-input" placeholder="Hall Ticket" value={hallTicket} onChange={(e) => setHallTicket(e.target.value)} />
            </div>

            <div className="ott-field">
              <label>Status</label>
              <select className="ott-input" value={submitStatus} onChange={(e) => setSubmitStatus(Number(e.target.value))}>
                <option value={STATUS_COMPLETED}>Completed</option>
                <option value={STATUS_IN_PROGRESS}>In Progress/ Partial</option>
              </select>
            </div>
          </div>

          <div className="ott-search-row">
            <Button variant="primary" onClick={handleSearch} disabled={searching}>
              {searching ? <Spinner animation="border" size="sm" /> : <>Search <Send size={15} /></>}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="ott-results">
              {sc && (
                <div className="ott-criteria">
                  <span className="ott-criteria-title">Search Criteria:</span>{" "}
                  <span>Test: {sc.test.name} [{sc.test.id}]</span>
                  {sc.enquiryId && <span> | Enquiry Id: {sc.enquiryId}</span>}
                  {sc.studentCode && <span> | Student Code: {sc.studentCode}</span>}
                  {sc.hallTicket && <span> | Hall Ticket: {sc.hallTicket}</span>}
                  <span> | Status: {sc.testSubmitStatus}</span>
                </div>
              )}

              <div className="ott-count">
                {beans.length} record{beans.length === 1 ? "" : "s"} found with the given Search Criteria.
              </div>

              {beans.length === 0 && (
                <div className="ott-empty-actions">
                  <Button variant="warning" onClick={() => { setExtraCutoff(""); setExtraStudentCode(studentCode); setExtraModal(true); }}>
                    Add Extra Time <Clock size={15} />
                  </Button>
                </div>
              )}

              {beans.length > 0 && (
                <div className="ott-table-wrap">
                  <table className="ott-table">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Student Code</th>
                        <th>Status<div className="ott-th-sub">[Time Remaining]</div></th>
                        {showTime && (
                          <th>
                            <span className="ott-danger">Overwrite</span>
                            <div className="ott-th-sub">(Remaining Time, max {result?.testTime} mins)</div>
                          </th>
                        )}
                        <th>Cut Off</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {beans.map((b, idx) => (
                        <tr key={idx}>
                          <td>{b.name}</td>
                          <td>{b.keyword}</td>
                          <td>{b.status}</td>
                          {showTime && (
                            <td>
                              <div className="ott-remaining">
                                <input
                                  type="number"
                                  className="ott-input ott-input-sm"
                                  placeholder="Remaining Time"
                                  value={rowInputs[idx]?.elapsedTime ?? ""}
                                  onChange={(e) => setRowInput(idx, { elapsedTime: e.target.value })}
                                />
                                <span className="ott-help">in min</span>
                              </div>
                              {rowErrors[idx] && <div className="ott-error">{rowErrors[idx]}</div>}
                            </td>
                          )}
                          <td>
                            <input
                              type="datetime-local"
                              step={1}
                              className="ott-input ott-input-sm ott-cutoff"
                              value={toLocalInput(rowInputs[idx]?.cutoff ?? "")}
                              onChange={(e) => setRowInput(idx, { cutoff: fromLocalInput(e.target.value) })}
                            />
                            {!showTime && rowErrors[idx] && <div className="ott-error">{rowErrors[idx]}</div>}
                          </td>
                          <td>
                            <Button variant="info" size="sm" onClick={() => handleActivate(b, idx)}>
                              Activate <Check size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Extra Time modal */}
      <Modal show={extraModal} onHide={() => setExtraModal(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Extra Time</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ott-modal-field">
            <label>Student Code</label>
            <input className="ott-input" placeholder="Student Code" value={extraStudentCode} onChange={(e) => setExtraStudentCode(e.target.value)} />
          </div>
          <div className="ott-modal-field">
            <label>Cut Off</label>
            <input
              type="datetime-local"
              step={1}
              className="ott-input"
              value={toLocalInput(extraCutoff)}
              onChange={(e) => setExtraCutoff(fromLocalInput(e.target.value))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setExtraModal(false)}>Close</Button>
          <Button variant="primary" onClick={handleAddExtraTime}>Submit</Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm modal */}
      <Modal show={confirm.show} onHide={() => setConfirm({ show: false, message: "", onConfirm: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm?</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirm.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setConfirm({ show: false, message: "", onConfirm: null })}>No</Button>
          <Button variant="success" onClick={() => confirm.onConfirm && confirm.onConfirm()}>Yes</Button>
        </Modal.Footer>
      </Modal>

      {/* Alert / result modal */}
      <Modal show={alertModal.show} onHide={() => setAlertModal((p) => ({ ...p, show: false }))} centered>
        <Modal.Header closeButton>
          <Modal.Title>{alertModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={alertModal.success ? "ott-alert-success" : "ott-alert-danger"}>{alertModal.message}</div>
          {alertModal.details && alertModal.details.length > 0 && (
            <table className="ott-detail-table">
              <tbody>
                {alertModal.details.map((d) => (
                  <tr key={d.label}>
                    <th>{d.label}</th>
                    <td>{d.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setAlertModal((p) => ({ ...p, show: false }))}>OK</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OverwriteTestTimePage;
